import { toPng } from 'html-to-image';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { UpdateContentItemDocument, useUpdateContentItem } from '~/api/content-items';
import { useNotifications, usePersistentSnackbar } from '~/api/helpers';
import { AppForm, type AppFormSubmitProps } from '~/components/AppForm';
import { assert } from '~/lib/assert';

export const EditApp = () => {
  const { t } = useTranslation();
  const [updateApp] = useUpdateContentItem();
  const [updateThumbnail] = useUpdateContentItem(
    useNotifications(UpdateContentItemDocument, {
      failure: t('common.thumbnail_messages.update_failed'),
      success: t('common.thumbnail_messages.update_success'),
    }),
  );
  const { appId } = useParams<{ appId: string }>();
  const persistentSnackbar = usePersistentSnackbar();

  const handleCapture = useCallback(async () => {
    const previewAppElement = document
      .querySelector<HTMLIFrameElement>('iframe#preview')
      ?.contentWindow?.document.body.querySelector<HTMLIFrameElement>('iframe')?.contentWindow
      ?.document.body;

    if (!previewAppElement) throw Error('Cannot find preview element');

    try {
      const thumbnailUri = await toPng(previewAppElement, {
        fetchRequestInit: { cache: 'no-cache' },
        pixelRatio: 1,
        style: { transform: 'none' },
      });

      void updateThumbnail({
        variables: { contentItemId: Number(appId), patch: { thumbnailUri } },
      });
    } catch (e) {
      persistentSnackbar(t('common.thumbnail_messages.failed'));
    }
  }, [appId, persistentSnackbar, t, updateThumbnail]);

  const handleSubmit = useCallback(
    async ({ patch }: AppFormSubmitProps) => {
      assert(appId != null);
      await updateApp({ variables: { contentItemId: Number(appId), patch } });
    },
    [appId, updateApp],
  );

  return <AppForm id={Number(appId)} onCapture={handleCapture} onSubmit={handleSubmit} />;
};
